import { useEffect, useState } from 'react'
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from 'react-redux';
import Backdrop from '../../common/modal/Backdrop';
import Modal from '../../common/Modal';
import ModalInnerWrapper from '../../common/ModalInnerWrapper';
import PannelBody from '../../fragments/PannelBody';
import InputBox from '../../common/interactive/InputBox';
import Button from '../../common/Button';
import DatepickerWithLabel from '../../common/interactive/DatepickerWithLabel';
import CheckboxCell from '../../common/interactive/CheckboxCell';
import { formatDate, formatSSN, hideSSNString, isPersonAMinor, phoneFormat } from '../../../lib/script';
import { countiesOptions, getNameSuffixOptions, getPreferedContactsOptions, patientRelationsOptions, resolvePatientRelationValue, stateOptions } from '../../../lib/systemData';
import FalseInputNumber from '../../common/FalseInputNumber';
import { caseQuickUpdate, getPostalInformation } from '../../../lib/fetchApi';
import { applyPostalInformation, applyPostalFormState, applyManualStateChange } from '../../../lib/postalInformation';
import { buildPatientFormContact } from '../../../lib/countyResolution';
import { useContactCountyConfirmation } from './countyMismatchConfirm';
import { notificationActions } from '../../../store/notifications';
import { caseActions } from '../../../store/case';
import CustomSelect from '../../common/interactive/CustomSelect';
import PannelFooter from '../../fragments/PannelFooter';

import classes from './EditPatientData.module.css'
import { isEmail } from '../../../lib/validation';
import { useUserTabEditPermissionDisabled } from '../../../lib/usersPermissions';
import Tag from '../../fragments/Tag';
import FormField from './FormField';

const genderOptions = [
    {
        label: 'Female',
        value: 'F'
    },
    {
        label: 'Male',
        value: 'M'
    }
]

export default function EditPatientData({}) {

    const data = useSelector(state => state.case.activeCase)

    let userPermissionPatientEdit = useUserTabEditPermissionDisabled('patient_edit')
    let disableFields = !userPermissionPatientEdit

    const dispatch = useDispatch()
    let contactInformation = data?.patient?.contact_information

    const userToken = useSelector(state => state.user.token)
    const activeCase = useSelector(state => state.case.activeCase)

    const [showModal, setShowModal] = useState(false)
    const [loading, setLoading] = useState(false)

    const [counties, setCounties] = useState(null)
    const [dob, setDob] = useState(data?.patient?.dob)
    const [guardianRel, setGuardianRel] = useState(resolvePatientRelationValue(data?.patient?.guardian_relationship))
    const [isDisabled, setIsDisabled] = useState(Boolean(data?.patient?.is_disabled))
    const [selectedState, setSelectedState] = useState(null)
    const [selectedcounty, setSelectedcounty] = useState(null)
    const [selectedCity, setSelectedCity] = useState(null)

    const { confirmCountyOnSave, modal: countyMismatchModal } = useContactCountyConfirmation({ showLienNote: true })

    const isMinorPatient = Boolean(data?.patient?.is_minor) || isPersonAMinor(dob)
    const showGuardianFields = isMinorPatient || isDisabled

    // Sync derived local state when switching active case/patient
    useEffect(() => {
        setDob(data?.patient?.dob ?? null)
        setGuardianRel(resolvePatientRelationValue(data?.patient?.guardian_relationship))
        setIsDisabled(Boolean(data?.patient?.is_disabled))
    }, [data?.patient?.id])

    function openModalHandler() {
        setShowModal(true)
    }
    function closeModalHandler() {
        setShowModal(false)
    }

    function changeDOBHandler(value) {
        setDob(value.data.value)
    }
    function guardianRelationHandler(value) {
        setGuardianRel(value.data.value)
    }

    function changeIsDisabledHandler(value) {
        setIsDisabled(value.data.value)
    }

    async function editInformationsHandler(event) {
        event.preventDefault()
        
        if (userPermissionPatientEdit) {
            setLoading(true)
            try {

            const fd = new FormData(event.target)

            const formData = Object.fromEntries(fd.entries())

            let errors = []

            if (formData && !formData.gender) {
                errors.push('Please check Gender Field')
            } 

            if (formData && formData.email && !isEmail(formData.email)) {
                errors.push('Please check Email Field')
            }

            // if (formData && formData.cell_phone && !isCompletePhone(formData.cell_phone)) {
            //     errors.push('Please check Cell Phone Field')
            // }

            if (errors && !errors.length) {
                const formContact = buildPatientFormContact(formData)

                const countyResult = await confirmCountyOnSave({
                    token: userToken,
                    originalContact: contactInformation,
                    formContact,
                    selectedCountyId: selectedcounty?.value ?? contactInformation?.county_id ?? null,
                    selectedCountyLabel: selectedcounty?.label ?? contactInformation?.county ?? '',
                    countiesOptions,
                    onSuggestedCounty: setSelectedcounty,
                })

                if (countyResult?.error) {
                    dispatch(notificationActions.setError(countyResult.error))
                    return
                }

                if (countyResult.cancelled) {
                    return
                }

                const countyToSave = {
                    value: countyResult.countyId,
                    label: countyResult.countyLabel,
                }

                const reqBody = {
                    type: 'case_patient',
                    fields: {
                        first_name: formData.first_name,
                        last_name: formData.last_name,
                        middle_name: formData.middle_name,
                        suffixId: formData.suffixId || null,
                        dob: formatDate(formData.dob),
                        gender: formData.gender, 
                        driver_license: formData.driver_license,
                        driver_license_first_name: formData.driver_license_first_name,
                        driver_license_last_name: formData.driver_license_last_name,
                        is_disabled: formData.is_disabled ? 1 : 0,
                        guardian_name: formData.guardian_name,
                        guardian_relationship: formData.guardian_relationship,
                        contact_preference: formData.contact_preference,
                        opted_in_sms: formData.opted_in_sms ? 1 : 0,
                        opted_in_voice: formData.opted_in_voice ? 1 : 0,
                        contact_information: {
                            email: formData.email,
                            phone: formData.phone,
                            phone_ext: formData.ext,
                            cell_phone: formData.cell_phone,
                            phone_2: formData.phone_2,
                            fax: formData.fax,
                            address_1: formContact.address_1,
                            address_2: formContact.address_2,
                            state: formContact.state,
                            county: countyToSave?.label,
                            county_id: countyToSave?.value,
                            city: formContact.city,
                            zip: formContact.zip
                        }
                    }
                }

                let update = await caseQuickUpdate(userToken, activeCase.case.id, reqBody)
                
                if (update && update.errorMessage) {
                    dispatch(notificationActions.setError(update))
                } else if (update?.staleResponse) {
                    return
                } else {
                    if (update?.data) {
                        dispatch(caseActions.replacePatientData(update.data))
                    }
                    dispatch(notificationActions.setError({errorMessage: `Your changes have been successfully saved to the case.`, type: 'success'}))
                    setShowModal(false)
                }
            } else {
                dispatch(notificationActions.setError({errorMessage : errors}))
            }

            } finally {
                setLoading(false)
            }
        }
    }

    const selectStatehandler = (value) => {
        applyManualStateChange({
            stateValue: value.data.value,
            stateOption: value.option,
            setCounties,
            setSelectedState,
            setSelectedcounty,
            countiesOptions,
            statesOptions: stateOptions,
        })
    }
    const selectCountyHandler = (value) => {
        setSelectedcounty(value.option)
    }

    useEffect(() => {
        if (contactInformation) {
            let selectedState = stateOptions().find(item => item.value == contactInformation.state)
            setTimeout(() => {
                setSelectedState(selectedState)
            }, 200);
            

            let counties = countiesOptions().filter(item => item.state == contactInformation.state)
            
            setTimeout(() => {
                setCounties(counties)
            }, 200);

            let county = countiesOptions().find(item => item.value == contactInformation.county_id)

            if (county) {
                setTimeout(() => {
                    setSelectedcounty(county)
                }, 300);
            }

            setSelectedCity(data.patient?.contact_information?.city)
        }

        
    }, [contactInformation])

    async function retrievePostalInformation(value) {
        setLoading(true)
        try {
            if (value && value.data?.value && value.data.value.length == 5) {
                let body = {
                    zip : value.data.value
                }
                let postalInformation = await getPostalInformation(userToken, body)

                if (postalInformation && postalInformation.zip) {
                    const result = applyPostalInformation({
                        postalInformation,
                        previousState: selectedState?.value,
                        previousCounty: selectedcounty,
                        statesOptions: stateOptions,
                        countiesOptions,
                    })

                    applyPostalFormState({
                        result,
                        setSelectedState,
                        setSelectedCity,
                        setCounties,
                        setSelectedcounty,
                    })
                } else {
                    dispatch(notificationActions.setError({errorMessage: 'No information found for the provided zipcode'}))
                }
            }
        } finally {
            setLoading(false)
        }
    }

    return <div className={classes.container}>
        {countyMismatchModal}
        <button title='edit patient data' onClick={openModalHandler}>
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                <path d="M4 4.625H3C2.46957 4.625 1.96086 4.83571 1.58579 5.21079C1.21071 5.58586 1 6.09457 1 6.625V15.625C1 16.1554 1.21071 16.6641 1.58579 17.0392C1.96086 17.4143 2.46957 17.625 3 17.625H12C12.5304 17.625 13.0391 17.4143 13.4142 17.0392C13.7893 16.6641 14 16.1554 14 15.625V14.625" stroke="#577988" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 2.62511L16 5.62511M17.385 4.21011C17.7788 3.81626 18.0001 3.28209 18.0001 2.72511C18.0001 2.16812 17.7788 1.63395 17.385 1.24011C16.9912 0.846261 16.457 0.625 15.9 0.625C15.343 0.625 14.8088 0.846261 14.415 1.24011L6 9.62511V12.6251H9L17.385 4.21011Z" stroke="#577988" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        </button>
        {showModal && <>
            {ReactDOM.createPortal(
                <Backdrop hide={closeModalHandler} />,
                document.getElementById("backdrop-root")
            )}  
            {ReactDOM.createPortal(
                <Modal>
                    <ModalInnerWrapper closeModal={closeModalHandler} modalTitle={'Edit information'}>       
                        <form onSubmit={editInformationsHandler}>                 
                            <PannelBody padding='large' background={'var(--line-transparent)'}>
                                {/* <div className={`row`}>
                                    <CheckboxCell label={'Is disabled'} name={'is_disabled'} direction={'reverse'} checked={isDisabled} isDisabled={disableFields} action={changeIsDisabledHandler}/>
                                </div> */}
                                    {isMinorPatient && <div className={`row flex flex_half`}>
                                        <Tag>Is a minor</Tag>
                                    </div>}  
                                    <div className={`row flex flex_half`}>
                                        {/* <InputBox label={'First Name'} name={'first_name'} required={true} currentValue={data?.patient?.first_name} isNotModifiable={disableFields} redBorderFlag={!data?.patient?.first_name}/>
                                        <InputBox label={'Last Name'} name={'last_name'} required={true} currentValue={data?.patient?.last_name} isNotModifiable={disableFields} redBorderFlag={!data?.patient?.last_name}/> */}
                                        <FormField label={'First Name'} required={true} type={'input'} value={data?.patient?.first_name} isDisabled={disableFields} name={'first_name'}/>
                                        <FormField label={'Last Name'} required={true} type={'input'} value={data?.patient?.last_name} isDisabled={disableFields} name={'last_name'}/>
                                    </div>
                                    <div className={`row flex flex_half`}>
                                        <FormField label={'Middle Name'} type={'input'} value={data?.patient?.middle_name} isDisabled={disableFields} name={'middle_name'}/>
                                        <FormField label={'Suffix'} type={'select'} value={data?.patient?.suffix_id || data?.patient?.suffix || ''} isDisabled={disableFields} name={'suffixId'} optionsData={getNameSuffixOptions(data?.patient)} rtlDrawer={true}/>
                                    </div>
                                    <div className={`row flex flex_half`}>
                                        {/* <DatepickerWithLabel required={true} label={'Date of Birth'} defaultDate={dob} name={'dob'} action={changeDOBHandler} disabled={disableFields}/> */}
                                        <FormField label={'Date of Birth'} required={true} type={'date'} value={dob} isDisabled={disableFields} name={'dob'} hasAction={changeDOBHandler}/>
                                        <CheckboxCell label={'Is disabled'} name={'is_disabled'} direction={'reverse'} checked={isDisabled} isDisabled={disableFields} action={changeIsDisabledHandler}/>
                                                                              
                                    </div>
                                    {showGuardianFields && <div className={`row flex flex_half`}>
                                        {/* <InputBox label={'Guardian Name'} name={'guardian_name'} required={true} currentValue={data?.patient?.guardian_name} isNotModifiable={disableFields} redBorderFlag={!data?.patient?.guardian_name}/> */}
                                        <FormField label={'Guardian Name'}  type={'input'} value={data?.patient?.guardian_name} isDisabled={disableFields} name={'guardian_name'}/>
                                        {/* <CustomSelect required={true} label={'Guardian Relationship'} name={'guardian_relationship'} optionsData={patientRelationsOptions()} preSelectedValue={guardianRel} rtlDrawer={true} action={guardianRelationHandler} isDisabled={disableFields}/> */}
                                        <FormField label={'Guardian Relationship'} type={'select'} value={guardianRel} isDisabled={disableFields} name={'guardian_relationship'} optionsData={patientRelationsOptions()} rtlDrawer={true}/>
                                    </div>}
                                    <div className={`row flex flex_half`}>
                                        <InputBox
                                            label={'SSN'}
                                            currentValue={hideSSNString(formatSSN(data?.patient?.ssn)) || ''}
                                            isNotModifiable={true}
                                            redBorderFlag={!data?.patient?.ssn}
                                        />
                                        {/* <CustomSelect label={'Gender at Birth'} required={true} optionsData={genderOptions} preSelectedValue={data?.patient?.gender} name={'gender'} rtlDrawer={true} isDisabled={disableFields}/> */}
                                        <FormField label={'Gender at Birth'} required={true} type={'select'} value={data?.patient?.gender} isDisabled={disableFields} name={'gender'} optionsData={genderOptions} rtlDrawer={true}/>
                                    </div>

                                    <div className={`row flex flex_half`}>
                                        <InputBox label={"Driver License First Name"} name={'driver_license_first_name'} currentValue={data?.patient?.driver_license_first_name} isNotModifiable={disableFields}/>
                                        <InputBox label={"Driver License Last Name"} name={'driver_license_last_name'} currentValue={data?.patient?.driver_license_last_name} isNotModifiable={disableFields}/>
                                    </div>  


                                    <div className={`row flex flex_half`}>
                                        <InputBox label={"Driver’s License"} name={'driver_license'} currentValue={data?.patient?.driver_license} isNotModifiable={disableFields}/>
                                        
                                    </div>        
                                    
                                
                            </PannelBody>
                            <PannelBody>
                                <div className={`row`}>
                                    <h3>Contact Details</h3>
                                </div>
                                <div className={`row`}>
                                    <small>
                                        <i className="ti ti-alert-hexagon" aria-hidden="true"></i>{' '}
                                        Please check in NextGen that Notification Preference is selected (true). Otherwise, changes to Opted In for SMS and Opted In for Voice will not be saved.
                                    </small>
                               
                                </div>
                                <div className={`row`}>
                                    <CustomSelect label={'Preferred Contact'} optionsData={getPreferedContactsOptions()} preSelectedValue={data?.patient?.contact_preference} name={'contact_preference'} isDisabled={disableFields}/>
                                </div>
                                <div className={`row flex`}>
                                    <div className={'flex_1'}><CheckboxCell label={'Opted In for SMS'} name={'opted_in_sms'} direction={'reverse'} checked={data?.patient?.opted_in_sms} isDisabled={disableFields}/></div>
                                    <div className={'flex_1'}><CheckboxCell label={'Opted In for Voice'} name={'opted_in_voice'} direction={'reverse'} checked={data?.patient?.opted_in_voice} isDisabled={disableFields}/></div>
                                </div>
                                <div className={`row`}>
                                    {/* <InputBox label={'Email'} name={'email'} currentValue={data?.patient?.contact_information?.email} isNotModifiable={disableFields} redBorderFlag={!data?.patient?.contact_information?.email}/> */}
                                    <FormField label={'Email'} type={'input'} value={data?.patient?.contact_information?.email} isDisabled={disableFields} name={'email'}/>
                                </div>
                                <div className={`row`}>
                                    {/* <InputBox label={'Cell Phone'} name={'cell_phone'} currentValue={phoneFormat(data?.patient?.contact_information?.cell_phone)} format={'phone'} isNotModifiable={disableFields} redBorderFlag={!data?.patient?.contact_information?.cell_phone}/> */}
                                    <FormField label={'Cell Phone'} type={'input'} value={phoneFormat(data?.patient?.contact_information?.cell_phone)} isDisabled={disableFields} name={'cell_phone'} format={'phone'}/>
                                </div>
                                <div className={`row flex`}>
                                    <div className={'flex_5'}>
                                        <InputBox label={'Day Phone'} name={'phone'} currentValue={phoneFormat(data?.patient?.contact_information?.phone)} format={'phone'} isNotModifiable={disableFields} />
                                        {/* <FormField label={'Day Phone'} type={'input'} value={phoneFormat(data?.patient?.contact_information?.phone)} isDisabled={disableFields} name={'phone'} format={'phone'}/> */}
                                        </div>
                                    <div className={'flex_1'}>
                                        <InputBox label={'Ext'} name={'ext'}  currentValue={data?.patient?.contact_information?.phone_ext} isNotModifiable={disableFields} removeModalPosition={'right'}/>
                                    </div>
                                </div>
                                <div className={`row flex`}>
                                    <div className={'flex_5'}>
                                        <InputBox label={'Home Phone'} name={'phone_2'} currentValue={phoneFormat(data?.patient?.contact_information?.phone_2)} format={'phone'} isNotModifiable={disableFields} />
                                        {/* <FormField label={'Home Phone'} type={'input'} value={phoneFormat(data?.patient?.contact_information?.phone_2)} isDisabled={disableFields} name={'phone_2'} format={'phone'}/> */}
                                    </div>
                                    
                                </div>
                                
                                <div className={`row`}>
                                    <InputBox label={'Fax'} name={'fax'} currentValue={phoneFormat(data?.patient?.contact_information?.fax)} format={'phone'} isNotModifiable={disableFields}/>
                                    {/* <FormField label={'Fax'} type={'input'} value={phoneFormat(data?.patient?.contact_information?.fax)} isDisabled={disableFields} name={'fax'} format={'phone'}/> */}
                                </div>
                                <div className={`row`}>
                                    {/* <InputBox label={'Address Line 1'} name={'address_line_1'} currentValue={data?.patient?.contact_information?.address_1} isNotModifiable={disableFields} redBorderFlag={!data?.patient?.contact_information?.address_1}/> */}
                                    <FormField label={'Address Line 1'} type={'input'} value={data?.patient?.contact_information?.address_1} isDisabled={disableFields} name={'address_line_1'}/>
                                </div>
                                <div className={`row`}>
                                    <InputBox label={'Address Line 2(optional)'} name={'address_line_2'} currentValue={data?.patient?.contact_information?.address_2} isNotModifiable={disableFields}/>
                                </div>
                                <div className={`row flex flex_half`}>
                                    <InputBox label="Zip Code" type="text" name="location_zip_code"  placeholder="" currentValue={data?.patient?.contact_information?.zip} format={'zip'} action={retrievePostalInformation} isNotModifiable={disableFields} redBorderFlag={!data?.patient?.contact_information?.zip}/>
                                    <InputBox label="City" type="text" name="location_city" placeholder="" currentValue={selectedCity} action={(value) => setSelectedCity(value.data.value)} isNotModifiable={disableFields} redBorderFlag={!selectedCity}/>
                                </div>
                                <div className={`row flex flex_half`}>
                                    <CustomSelect label={'State'} optionsData={stateOptions()} preSelectedValue={selectedState?.value} name={'state'} action={selectStatehandler} reverseDrawer={true} isDisabled={disableFields} hasRemove={true} borderRed={!selectedState?.value || selectedState?.value == ''}/>
   
                                    {counties?.length > 0 && <CustomSelect key={selectedState?.value ?? 'county'} label={'County'} optionsData={counties} preSelectedValue={selectedcounty?.value} name={'county'} action={selectCountyHandler} reverseDrawer={true} rtlDrawer={true} isDisabled={disableFields} hasRemove={true} borderRed={!selectedcounty?.value || selectedcounty?.value == ''}/>}

                                    {(!counties || counties.length === 0) && <FalseInputNumber label={'County'}/>}
                                </div>
                               
                                
                            </PannelBody>
                            {userPermissionPatientEdit && <PannelFooter>
                                <div className={`row`}>
                                    <Button btnStyle={'full'} type={'submit'} isLoading={loading}>Save</Button>
                                </div>
                            </PannelFooter>}
                        </form>
                    </ModalInnerWrapper>
                </Modal>,
                document.getElementById("overlay-root")
            )}          
        </>}
    </div>
}